import { useEffect, useState } from 'react';
import { useAsyncEffect } from 'rooks';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type { ItemSignalAndCondition } from '@/summaryReport/summaryReport.actions';
import { fetchItemsSignalAndConditionMap } from '@/summaryReport/summaryReport.actions';
import { findItemIn, getTrendStores } from '@/trend/trendDataHelper.utilities';
import type { ConditionItem, SeriesItem } from '@/trendData/trendData.types';

/**
 * Updates an item with properties from the trend store if they differ.
 * Returns the updated item if changes were made, undefined otherwise.
 * When trendItem is undefined (item removed from store), removes color if present.
 */
function updateItemFromStore(
  item: ItemSignalAndCondition['signal'] | ItemSignalAndCondition['condition'],
  trendItem: SeriesItem | ConditionItem | undefined,
): typeof item | undefined {
  let itemWasUpdated = false;
  const updatedItem = { ...(item ?? {}) };

  if (!trendItem?.color && item?.color) {
    delete updatedItem.color;
    itemWasUpdated = true;
  } else if (trendItem && trendItem?.color !== item?.color) {
    updatedItem.color = trendItem.color;
    itemWasUpdated = true;
  }

  if (trendItem?.name && item?.name !== trendItem?.name) {
    updatedItem.name = trendItem.name;
    itemWasUpdated = true;
  }

  return itemWasUpdated ? updatedItem : undefined;
}

/**
 * Merges the fetched item signal and condition map with updates from the trend stores.
 * Updates signal and condition properties (color, name) based on current store state.
 *
 * @param fetchedMap - The map fetched from the API
 * @returns The merged map with store updates applied
 */
function mergeMapWithStoreUpdates(
  fetchedMap: Record<string, ItemSignalAndCondition>,
): Record<string, ItemSignalAndCondition> {
  if (Object.keys(fetchedMap).length === 0) {
    return fetchedMap;
  }

  let wasUpdated = false;
  const updatedMap = { ...fetchedMap };

  Object.values(updatedMap).forEach((entry) => {
    const trendSignal = findItemIn(getTrendStores(), entry.signal?.id) as SeriesItem | undefined;
    const trendCondition = findItemIn(getTrendStores(), entry.condition?.id) as ConditionItem | undefined;

    const updatedSignal = updateItemFromStore(entry.signal, trendSignal);
    if (updatedSignal) {
      entry.signal = updatedSignal;
      wasUpdated = true;
    }

    const updatedCondition = updateItemFromStore(entry.condition, trendCondition);
    if (updatedCondition) {
      entry.condition = updatedCondition;
      wasUpdated = true;
    }
  });

  return wasUpdated ? updatedMap : fetchedMap;
}

/**
 * Custom hook that fetches and maintains the item signal and condition map,
 * updating it whenever items, trendSignals, or trendConditions change.
 *
 * @param items - Array of statistics summary items
 * @param trendSignals - Array of trend signals from the store
 * @param trendConditions - Array of trend conditions from the store
 * @returns The current itemSignalAndConditionMap
 */
export function useItemSignalAndConditionMap(
  items: StatisticsSummaryItem[],
  trendSignals: any[],
  trendConditions: any[],
): Record<string, ItemSignalAndCondition> {
  const [fetchedItemSignalAndConditionMap, setFetchedItemSignalAndConditionMap] = useState<
    Record<string, ItemSignalAndCondition>
  >({});
  const [itemSignalAndConditionMap, setItemSignalAndConditionMap] = useState<Record<string, ItemSignalAndCondition>>(
    {},
  );

  useAsyncEffect(async () => {
    if (items.length === 0) {
      setItemSignalAndConditionMap({});
      return;
    }

    const signalAndConditionMap = await fetchItemsSignalAndConditionMap(items);
    setFetchedItemSignalAndConditionMap(signalAndConditionMap);
  }, [items]);

  useEffect(() => {
    const mergedMap = mergeMapWithStoreUpdates(fetchedItemSignalAndConditionMap);
    setItemSignalAndConditionMap(mergedMap);
  }, [fetchedItemSignalAndConditionMap, trendSignals, trendConditions]);

  return itemSignalAndConditionMap;
}
